import React, { useContext } from 'react'
import { generatePath } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { clientTestTypes } from 'api/Clients/Types/TestQuestionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { FlexBox } from 'components/Box'
import MoreButton from 'components/MoreButton/MoreButton'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'

interface Props {
  data: ClientDetailedDto
}
const ClientIBTestPage: React.FC<Props> = ({ data }) => {
  const clientsApiClient = useApiClient(ClientsApiClient)
  const [auth] = useContext(AuthSessionContext)

  const { callback: clientTests } = useCallbackWithForceRefresh(
    () => clientsApiClient.getClientIbInfoTestAnswers(data.id),
    [data.id]
  )
  const { data: tests } = useFetchOne(clientTests)
  const permissions = auth?.permissions || []

  const handleOpenFilledTest = (gridCellProps: GridCellProps) => {
    const testPath = generatePath(links.clientTestFilledWindow.detailsUrl as string, {
      type: clientTestTypes.ib,
      clientId: data.id ? data.id : '',
      clientStatusId: data.status.id,
      testId: gridCellProps.dataItem.id ? gridCellProps.dataItem.id : '',
    })
    window.open(testPath, '_blank', 'location=yes,height=500,width=520,scrollbars=yes,status=yes')
  }

  const handleEditTestForm = (testId?: string) => {
    if (!hasPermission(permissions, PermissionsEnum.CanAddClientIBInformation)) return
    const testPath = generatePath(links.clientEditTestFormWindow.detailsUrl as string, {
      type: clientTestTypes.ib,
      clientId: data.id ? data.id : '',
      testId: testId || '',
    })
    window.open(testPath, '_blank', 'location=yes,height=500,width=520,scrollbars=yes,status=yes')
  }

  const handleAddNewTest = () => {
    if (!hasPermission(permissions, PermissionsEnum.CanAddClientIBInformation)) return
    const testPath = generatePath(links.clientNewTestFormWindow.detailsUrl as string, {
      type: clientTestTypes.ib,
      clientId: data.id ? data.id : '',
    })
    window.open(testPath, '_blank', 'location=yes,height=500,width=520,scrollbars=yes,status=yes')
  }

  const isClientActive = () => data?.status.id === 4

  const isAlreadyCreatedIbInformation = () => clientTests.length > 0
  return (
    <React.Fragment>
      <FlexBox style={{ marginBottom: 36 }} alignItems='center' justifyContent='flex-start'>
        <Button
          disabled={
            !hasPermission(permissions, PermissionsEnum.CanAddClientIBInformation) ||
            !isClientActive() ||
            isAlreadyCreatedIbInformation()
          }
          onClick={() => handleAddNewTest()}
        >
          Add new
        </Button>
      </FlexBox>
      <Grid data={tests || []} resizable>
        <Column
          field='test.name'
          title='Name'
          className='table-columns__content'
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__link' onClick={() => handleOpenFilledTest(gridCellProps)}>
              {gridCellProps.dataItem.test.name}
            </td>
          )}
        />
        <Column title='User' field='user.name' className='table-columns__content' />
        <Column
          field='dateTaken'
          title='Date Submitted'
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__date'>
              {formatLocalDateUTC(gridCellProps.dataItem.dateTaken, true)}
            </td>
          )}
        />
        {hasPermission(permissions, PermissionsEnum.CanAddClientIBInformation) && (
          <Column
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      'data-test': 'edit',
                      onClick: () => handleEditTestForm(gridCellProps.dataItem.id),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        )}
      </Grid>
    </React.Fragment>
  )
}
// TODO: update these export defaults to match

export default ClientIBTestPage
